import mergeRefs from 'merge-refs';
import type { ComponentChildren, RefObject } from 'preact';
import { createPortal, type HTMLAttributes, useImperativeHandle, forwardRef } from 'preact/compat';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { useSignal } from '@preact/signals';
import { CommonContainer } from './containers/CommonContainer';
import { cn } from 'utils/cn';
import { useKeyboardControls } from 'components/dropdown/useKeyboardControls.ts';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Dropdown {
	export type Placement = (typeof placement)[number];

	export interface RenderContainerProps<TOption = unknown> {
		isOpen: boolean;
		toggleOpen: (event?: MouseEvent) => void;
		children?: ComponentChildren;
		mobileTitle?: ComponentChildren;
		dropdownRef: RefObject<HTMLDivElement>;
		clickOutsideRef?: RefObject<HTMLDivElement>;
		placement: Placement;
		dataTest?: string;
		className?: string;
		value?: TOption;
		aria?: {
			role?: HTMLAttributes<any>['role'];
		};
		id?: string;
		isMobileFullscreen?: boolean;
	}

	export interface RenderOpenerProps<TOption = unknown> {
		toggleOpen: (event?: MouseEvent) => void;
		open: () => void;
		openerRef: RefObject<HTMLButtonElement>;
		value?: TOption;
		onKeyDown: (event: KeyboardEvent) => void;
		isOpen: boolean;
		aria?: {
			role?: HTMLAttributes<any>['role'];
			'aria-labelledby'?: HTMLAttributes<any>['aria-labelledby'];
			'aria-autocomplete'?: HTMLAttributes<any>['aria-autocomplete'];
			'aria-activedescendant'?: HTMLAttributes<any>['aria-activedescendant'];
			'aria-controls'?: HTMLAttributes<any>['aria-controls'];
			'aria-expanded'?: HTMLAttributes<any>['aria-expanded'];
			'aria-haspopup'?: HTMLAttributes<any>['aria-haspopup'];
			'aria-label'?: HTMLAttributes<any>['aria-label'];
			'aria-owns'?: HTMLAttributes<any>['aria-owns'];
		};
	}

	export interface RenderOptionProps<TOption = unknown> {
		className?: string;
		iconSize?: number;
		option: TOption;
		index: number;
		props: {
			onClick: (event: MouseEvent) => void;
			onMouseEnter: () => void;
			onMouseLeave: () => void;
			forceClose: () => void;
		};
		value?: TOption;
		isFocused: boolean;
		aria?: {
			role?: HTMLAttributes<any>['role'];
			'aria-label'?: HTMLAttributes<any>['aria-label'];
		};
		id?: string;
	}

	export type OptionSelectedHandler<TOption> = (event: Event, newValue: TOption, currentValue: TOption) => void;
}

interface Props<TOption = any> {
	dataTest?: string;
	renderOption?: (optionProps: Dropdown.RenderOptionProps<TOption>) => null | JSX.Element;
	renderContainer?: (containerProps: Dropdown.RenderContainerProps<TOption>) => JSX.Element;
	renderOpener: (openerProps: Dropdown.RenderOpenerProps<TOption>) => JSX.Element;
	children?: ComponentChildren;
	options?: readonly TOption[];
	optionsTitle?: ComponentChildren;
	value?: TOption;
	onOptionSelected?: Dropdown.OptionSelectedHandler<TOption>;
	placement?: Dropdown.Placement;
	className?: string;
	closeOnMouseLeave?: boolean;
	mobileTitle?: ComponentChildren;
	isMobileFullscreen?: boolean;
	wrapperClassName?: string;
	wrapperPortalRefElement?: RefObject<HTMLDivElement>;
	containerPortalRefElement?: RefObject<HTMLElement>;
	outsideClickRef?: RefObject<HTMLElement>[];
	idPrefix?: string;
	containerAriaRole?: HTMLAttributes<any>['role'];
	openerAriaRole?: HTMLAttributes<any>['role'];
	openerAriaLabel?: HTMLAttributes<any>['aria-label'];
	optionAriaRole?: HTMLAttributes<any>['role'];
	onOpen?: () => void;
	onClose?: () => void;
}

const RESIZE_EVENT_OPTIONS: AddEventListenerOptions = { passive: true };

export type ImperativeHandle = {
	close: () => void;
};

export const Dropdown = forwardRef<ImperativeHandle, Props>(
	(
		{
			dataTest,
			renderContainer = CommonContainer,
			renderOpener,
			renderOption,
			options,
			optionsTitle,
			value,
			onOptionSelected,
			className,
			placement = 'bottom-left',
			children,
			closeOnMouseLeave,
			mobileTitle,
			isMobileFullscreen,
			wrapperClassName,
			containerPortalRefElement,
			wrapperPortalRefElement,
			outsideClickRef,
			idPrefix,
			containerAriaRole,
			openerAriaRole,
			openerAriaLabel,
			optionAriaRole,
			onOpen,
			onClose,
		}: Props,
		ref,
	) => {
		const [isOpen, setIsOpen] = useState(false);
		const wrapperRef = useRef<HTMLDivElement>(null);
		const openerRef = useRef<HTMLButtonElement>(null);
		const { dropdownRef, adjustedPlacementSignal } = usePlacement({
			placement,
			isOpen,
			openerRef,
		});
		const { onMouseLeave, onMouseEnter } = useDelayedCloseOnMouseLeave(!!closeOnMouseLeave, () => close());
		const { focusedIndex, onKeyDown, setFocus, handleEscape } = useKeyboardControls({
			allowSelectWithSpace: true,
			numItems: options?.length ?? 0,
			onSelect: (event, index: number) => {
				if (isOpen) {
					close();
					setFocus(index);
					onOptionSelected?.(event ?? new Event('manual'), options![index], value);
				} else {
					open();
				}
			},
		});

		useEffect(() => {
			const handleClickOutside = (event: MouseEvent) => {
				if (
					isOpen &&
					wrapperRef.current &&
					!wrapperRef.current.contains(event.target as Node) &&
					(!outsideClickRef ||
						outsideClickRef.every((ref) => ref.current && !ref.current.contains(event.target as Node)))
				) {
					close();
					handleEscape();
				}
			};
			if (isOpen) {
				globalThis.document.addEventListener('click', handleClickOutside);
			} else {
				globalThis.document.removeEventListener('click', handleClickOutside);
			}

			return () => {
				globalThis.document.removeEventListener('click', handleClickOutside);
			};
		}, [isOpen]);

		useEffect(() => {
			const handleKeyPress = (event: KeyboardEvent) => {
				if (event.key === 'Escape' && isOpen) {
					close();
					handleEscape();
				}
			};

			globalThis.document.addEventListener('keydown', handleKeyPress);
			return () => globalThis.document.removeEventListener('keydown', handleKeyPress);
		}, [isOpen]);

		const handleDropdownClick =
			options &&
			useCallback(
				(event: Event, option: (typeof options)[number]) => {
					setIsOpen(!isOpen);
					if (isOpen) {
						onClose?.();
					} else {
						onOpen?.();
					}
					setFocus(options.indexOf(option));
					onOptionSelected?.(event, option, value);
				},
				[isOpen],
			);

		const toggleOpen = (event?: MouseEvent) => {
			// Could lead to form submit
			event?.preventDefault();

			setIsOpen(!isOpen);
			if (isOpen) {
				onClose?.();
			} else {
				onOpen?.();
			}
			setFocus(options?.indexOf(value) ?? 0);
		};

		const open = () => {
			setIsOpen(true);
			onOpen?.();
			setFocus(options?.indexOf(value) ?? 0);
		};

		const close = () => {
			setIsOpen(false);
			onClose?.();
		};

		useImperativeHandle(ref, () => ({
			close,
		}));

		const container = renderContainer({
			className,
			dataTest,
			isOpen,
			toggleOpen,
			dropdownRef,
			placement: adjustedPlacementSignal.value,
			mobileTitle,
			isMobileFullscreen,
			value,
			id: idPrefix ? `${idPrefix}-container` : undefined,
			aria: { role: containerAriaRole },
			children:
				options && renderOption ? (
					<>
						{optionsTitle}
						{options.map((option, index) => {
							const props = {
								onClick: (event: MouseEvent) => {
									if (handleDropdownClick) handleDropdownClick(event, option);
								},
								onMouseEnter: () => setFocus(index),
								onMouseLeave: () => setFocus(options.indexOf(value)),
								forceClose: close,
							};

							return renderOption({
								option,
								index,
								props,
								value,
								isFocused: index === focusedIndex,
								aria: { role: optionAriaRole },
								id: idPrefix ? `${idPrefix}-option-${index}` : undefined,
							});
						})}
					</>
				) : (
					children
				),
		});
		const opener = renderOpener({
			toggleOpen,
			openerRef,
			open,
			value,
			onKeyDown,
			isOpen,
			aria: {
				'aria-activedescendant': isOpen && idPrefix ? `${idPrefix}-option-${focusedIndex}` : undefined,
				'aria-controls': idPrefix ? `${idPrefix}-container` : undefined,
				'aria-expanded': isOpen,
				'aria-haspopup': 'listbox',
				'aria-label': openerAriaLabel,
				'aria-owns': idPrefix ? `${idPrefix}-container` : undefined,
				role: openerAriaRole,
			},
		});

		return (
			<div
				ref={mergeRefs(wrapperRef, wrapperPortalRefElement)}
				class={cn('relative', wrapperClassName)}
				onMouseLeave={onMouseLeave}
				onMouseEnter={onMouseEnter}
				data-test="dropdown-opener"
			>
				{opener}
				{containerPortalRefElement?.current &&
				getComputedStyle(containerPortalRefElement.current).display !== 'none' // this is for conditional rendering based on media queries
					? isOpen && createPortal(container, containerPortalRefElement.current)
					: isOpen && container}
			</div>
		);
	},
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const placement = ['top-left', 'top-right', 'bottom-left', 'bottom-right', 'bottom-center', 'top-center'] as const;

const usePlacement = ({
	placement,
	isOpen,
	openerRef,
}: {
	placement: Dropdown.Placement;
	isOpen: boolean;
	openerRef: RefObject<HTMLButtonElement>;
}) => {
	const dropdownRef = useRef<HTMLDivElement>(null);
	const adjustedPlacementSignal = useSignal(placement);

	const updatePlacement = useCallback(() => {
		if (!dropdownRef.current || !openerRef.current) {
			const notFound = [!dropdownRef.current && 'dropdownRef', !openerRef.current && 'openerRef'].filter(Boolean);
			console.error(`Dropdown's ${notFound.join(' and ')} is not set`);
			return;
		}

		const openerRect = openerRef.current.getBoundingClientRect();
		const dropdownWidth = dropdownRef.current.clientWidth;
		const dropdownHeight = dropdownRef.current.clientHeight;
		const viewportWidth = globalThis.window.innerWidth;
		const viewportHeight = globalThis.window.innerHeight;

		let newValue = placement;

		if (placement === 'top-left') {
			if (openerRect.top - dropdownHeight < 0 && openerRect.left - dropdownWidth < 0) {
				newValue = 'bottom-right';
			} else if (openerRect.top - dropdownHeight < 0) {
				newValue = 'bottom-left';
			} else if (openerRect.left - dropdownWidth < 0) {
				newValue = 'top-right';
			}
		} else if (placement === 'top-right') {
			if (openerRect.top - dropdownHeight < 0 && openerRect.right + dropdownWidth > viewportWidth) {
				newValue = 'bottom-left';
			} else if (openerRect.top - dropdownHeight < 0) {
				newValue = 'bottom-right';
			} else if (openerRect.right + dropdownWidth > viewportWidth) {
				newValue = 'top-left';
			}
		} else if (placement === 'top-center') {
			const halfDropdownWidth = dropdownWidth / 2;

			if (openerRect.top - dropdownHeight < 0 && openerRect.right + halfDropdownWidth > viewportWidth) {
				newValue = 'bottom-left';
			} else if (openerRect.top - dropdownHeight < 0 && openerRect.left - halfDropdownWidth < 0) {
				newValue = 'bottom-right';
			} else if (openerRect.top - dropdownHeight < 0) {
				newValue = 'bottom-center';
			} else if (openerRect.right + halfDropdownWidth > viewportWidth) {
				newValue = 'top-left';
			} else if (openerRect.left - halfDropdownWidth < 0) {
				newValue = 'top-right';
			}
		} else if (placement === 'bottom-left') {
			if (openerRect.bottom + dropdownHeight > viewportHeight && openerRect.left - dropdownWidth < 0) {
				newValue = 'top-right';
			} else if (openerRect.bottom + dropdownHeight > viewportHeight) {
				newValue = 'top-left';
			} else if (openerRect.left - dropdownWidth < 0) {
				newValue = 'bottom-right';
			}
		} else if (placement === 'bottom-right') {
			if (
				openerRect.bottom + dropdownHeight > viewportHeight &&
				openerRect.right + dropdownWidth > viewportWidth
			) {
				newValue = 'top-left';
			} else if (openerRect.bottom + dropdownHeight > viewportHeight) {
				newValue = 'top-right';
			} else if (openerRect.right + dropdownWidth > viewportWidth) {
				newValue = 'bottom-left';
			}
		} else if (placement === 'bottom-center') {
			const halfDropdownWidth = dropdownWidth / 2;
			if (
				openerRect.bottom + dropdownHeight > viewportHeight &&
				openerRect.right + halfDropdownWidth > viewportWidth
			) {
				newValue = 'top-left';
			} else if (openerRect.bottom + dropdownHeight > viewportHeight && openerRect.left - halfDropdownWidth < 0) {
				newValue = 'top-right';
			} else if (openerRect.bottom + dropdownHeight > viewportHeight) {
				newValue = 'top-center';
			} else if (openerRect.right + halfDropdownWidth > viewportWidth) {
				newValue = 'bottom-left';
			} else if (openerRect.left - halfDropdownWidth < 0) {
				newValue = 'bottom-right';
			}
		}

		adjustedPlacementSignal.value = newValue;
	}, [placement]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		// Wait to render component with new value of isOpen
		setTimeout(updatePlacement, 0);
	}, [updatePlacement, isOpen]);

	useEffect(() => {
		if (!isOpen) {
			globalThis.window.removeEventListener('resize', updatePlacement, RESIZE_EVENT_OPTIONS);
			return;
		}
		globalThis.window.addEventListener('resize', updatePlacement, RESIZE_EVENT_OPTIONS);

		return () => globalThis.window.removeEventListener('resize', updatePlacement, RESIZE_EVENT_OPTIONS);
	}, [updatePlacement, isOpen]);

	return { dropdownRef, adjustedPlacementSignal };
};

const useDelayedCloseOnMouseLeave = (enabled: boolean, closeCallback: () => void) => {
	if (!enabled) {
		return { onMouseEnter: undefined, onMouseLeave: undefined };
	}

	const leavingTimeoutId = useRef<NodeJS.Timeout>();
	const clearLeavingTimeout = () => {
		if (leavingTimeoutId.current) {
			clearTimeout(leavingTimeoutId.current);
			leavingTimeoutId.current = undefined;
		}
	};

	useEffect(() => {
		return clearLeavingTimeout;
	}, [closeCallback]);

	const onMouseLeave = () => {
		leavingTimeoutId.current = setTimeout(closeCallback, 500);
	};
	const onMouseEnter = clearLeavingTimeout;

	return { onMouseLeave, onMouseEnter };
};
